import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from 'styled-components';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { UserConversationProvider } from './context/UserConversationContext';
import { ThemeProvider as CustomThemeProvider, useTheme } from './context/ThemeContext';
import GlobalStyles from './styles/GlobalStyles';
import { getTheme } from './theme/theme';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ChatPage from './pages/ChatPage';
import ChatHistoryPage from './pages/ChatHistoryPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import MFASetupPage from './pages/MFASetupPage';
import LoadingPage from './pages/LoadingPage';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

const AppContent: React.FC = () => {
  const { isAuthenticated, loading, requiresMFA } = useAuth();
  const { theme } = useTheme();

  if (loading) {
    return <LoadingPage />;
  }

  const currentTheme = getTheme(theme);

  return (
    <ThemeProvider theme={currentTheme}>
      <GlobalStyles />
      <Router>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/chat" replace />
              ) : requiresMFA ? (
                <Navigate to="/mfa" replace />
              ) : (
                <LoginPage />
              )
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated ? (
                <Navigate to="/chat" replace />
              ) : (
                <RegisterPage />
              )
            }
          />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route
            path="/mfa"
            element={
              requiresMFA ? (
                <MFASetupPage />
              ) : isAuthenticated ? (
                <Navigate to="/chat" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Protected routes */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <UserConversationProvider>
                  <ChatProvider>
                    <ChatPage />
                  </ChatProvider>
                </UserConversationProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <UserConversationProvider>
                  <ChatHistoryPage />
                </UserConversationProvider>
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route
            path="/"
            element={
              <Navigate
                to={isAuthenticated ? "/chat" : "/login"}
                replace
              />
            }
          />

          {/* Catch all route */}
          <Route
            path="*"
            element={
              <Navigate
                to={isAuthenticated ? "/chat" : "/login"}
                replace
              />
            }
          />
        </Routes>
      </Router>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: theme === 'dark' ? '#2d333a' : '#ffffff',
            color: theme === 'dark' ? '#ffffff' : '#2d333a',
            border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#ffffff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff',
            },
          },
          loading: {
            iconTheme: {
              primary: '#10a37f',
              secondary: '#ffffff',
            },
          },
        }}
      />
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <CustomThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </CustomThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
